import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { STATUSES, statusMeta, sourceIcon, fmtMoney, fmtDate, SOURCES } from "../lib/constants";
import LeadDrawer from "../components/LeadDrawer";
import SavedSegments from "../components/SavedSegments";
import WebImportDialog from "../components/WebImportDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Loader2, Globe, Mail } from "lucide-react";

export default function Leads() {
  const [drawerId, setDrawerId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("all");
  const [hasEmail, setHasEmail] = useState("all");

  const { data: facets = { categories: [], locations: [] } } = useQuery({
    queryKey: ["lead-facets"],
    queryFn: () => api.get("/leads/facets").then((r) => r.data),
  });

  const { data: leads = [] } = useQuery({
    queryKey: ["leads", q, status, source, category, location, hasEmail],
    queryFn: () => {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (source !== "all") params.source = source;
      if (category !== "all") params.category = category;
      if (location !== "all") params.location = location;
      if (hasEmail !== "all") params.has_email = hasEmail;
      return api.get("/leads", { params }).then((r) => r.data);
    },
  });

  const qc = useQueryClient();
  const applySegment = (f) => {
    setQ(f.q || "");
    setStatus(f.status || "all");
    setSource(f.source || "all");
    setCategory(f.category || "all");
    setLocation(f.location || "all");
  };
  const [enriching, setEnriching] = useState(false);
  const [finding, setFinding] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const findEmails = async () => {
    setFinding(true);
    try {
      const r = await api.post("/leads/find-emails");
      toast.success(r.data.queued ? `Checking ${r.data.queued} websites for published emails…` : "No new websites to check");
      if (r.data.queued) setTimeout(() => qc.invalidateQueries({ queryKey: ["leads"] }), 15000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setFinding(false);
    }
  };
  const enrichFacebook = async () => {
    setEnriching(true);
    try {
      const r = await api.post("/leads/enrich-bulk", { sources: ["facebook", "instagram"], only_missing: true });
      toast.success(
        `Enriched ${r.data.enriched} lead${r.data.enriched === 1 ? "" : "s"} from Google` +
          (r.data.skipped ? ` · ${r.data.skipped} had no match` : "")
      );
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Leads</h1>
          <p className="text-muted-foreground mt-1">{leads.length} leads in your book.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setWebOpen(true)} variant="outline" data-testid="import-web-button">
            <Globe className="h-4 w-4 mr-1.5" /> Import from web
          </Button>
          <Button onClick={enrichFacebook} disabled={enriching} variant="outline" data-testid="enrich-facebook-button">
            {enriching ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <MapPin className="h-4 w-4 mr-1.5" />}
            Enrich Facebook leads
          </Button>
          <Button onClick={findEmails} disabled={finding} variant="outline" data-testid="find-emails-button">
            {finding ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Find emails
          </Button>
        </div>
      </div>
      <WebImportDialog open={webOpen} onOpenChange={setWebOpen} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email..." className="pl-9" data-testid="lead-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-44" data-testid="lead-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="sm:w-44" data-testid="lead-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-48" data-testid="lead-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-popover border-border max-h-72">
            <SelectItem value="all">All categories</SelectItem>
            {facets.categories.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.value} ({c.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="sm:w-52" data-testid="lead-location-filter"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent className="bg-popover border-border max-h-72">
            <SelectItem value="all">All locations</SelectItem>
            {facets.locations.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label} ({l.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={hasEmail} onValueChange={setHasEmail}>
          <SelectTrigger className="sm:w-36" data-testid="lead-email-filter"><SelectValue placeholder="Email" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Any email</SelectItem>
            <SelectItem value="yes">Has email</SelectItem>
            <SelectItem value="no">No email</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SavedSegments filters={{ q, status, source, category, location }} onApply={applySegment} />

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium text-right">Value</th>
                <th className="px-4 py-3 font-medium">Last contact</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const meta = statusMeta(lead.status);
                const Icon = sourceIcon(lead.source);
                return (
                  <tr
                    key={lead.id}
                    onClick={() => setDrawerId(lead.id)}
                    className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium">{lead.name}</p>
                      {lead.company && <p className="text-xs text-muted-foreground">{lead.company}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 capitalize text-muted-foreground">
                        <Icon className="h-4 w-4" /> {lead.source}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {lead.category && lead.category !== "Uncategorized" ? (
                        <span className="inline-flex items-center rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground/80">{lead.category}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {(lead.city || lead.province) && (
                        <p className="text-xs text-muted-foreground mt-1">{[lead.city, lead.province].filter(Boolean).join(", ")}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${meta.bg} ${meta.text} border ${meta.border}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{lead.phone || "—"}</td>
                    <td className="px-4 py-3 text-xs" data-testid={`lead-email-cell-${lead.id}`}>
                      {lead.email ? (
                        <>
                          <p className="text-foreground/80 truncate max-w-[220px]">{lead.email}</p>
                          {lead.email_unsubscribed ? (
                            <p className="text-rose-400 mt-0.5">unsubscribed</p>
                          ) : lead.email_source === "website" && (
                            <p className="text-emerald-400 mt-0.5">found on website</p>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-medium">{fmtMoney(lead.deal_value)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(lead.last_contacted_at)}</td>
                  </tr>
                );
              })}
              {leads.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">No leads found. Add one or import a CSV.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LeadDrawer leadId={drawerId} open={!!drawerId} onOpenChange={(v) => !v && setDrawerId(null)} />
    </div>
  );
}
